import React, { useState } from 'react';
import { Zap, Copy, Loader2, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { DIMENSION_CONFIG, getScoreGrade, getDimensionScoreColor } from '../../models/analysis';

export function OptimizationPanel({ analysis, onOptimize, optimizing }) {
  const opt = analysis.optimization;
  const [textExpanded, setTextExpanded] = useState(false);

  const handleCopy = () => {
    if (opt?.text) { navigator.clipboard.writeText(opt.text); toast.success('Optimized text copied'); }
  };

  if (!opt) {
    return (
      <div className="msml-slide-up" style={{ animationDelay: '0.2s' }}>
        <Button onClick={onOptimize} disabled={optimizing} className="msml-btn-primary w-full" data-testid="optimize-btn">
          {optimizing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Improving...</>
          ) : (
            <><Zap className="w-4 h-4 mr-2" />Improve for Human Benefit</>
          )}
        </Button>
      </div>
    );
  }

  const before = opt.beforeScores;
  const after = opt.afterScores;
  const deltaOverall = opt.deltaOverall || 0;
  const deltaDims = opt.deltaDimensions || {};

  const beforeHQ = before?.hq_score ?? analysis.hqScore;
  const afterHQ = after?.hq_score ?? beforeHQ;
  const afterGrade = getScoreGrade(afterHQ);
  const beforeGrade = getScoreGrade(beforeHQ);

  const dimKeys = Object.keys(before?.dimension_scores || analysis.dimensions?.reduce((a, d) => ({ ...a, [d.key]: d.score }), {}) || {});

  return (
    <div className="space-y-3 msml-slide-up" style={{ animationDelay: '0.2s' }}>
      {/* Overall HQ comparison */}
      <div className="msml-card !p-3 !gap-0 relative overflow-hidden" data-testid="hq-comparison-card">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, ${afterGrade.color} 0%, transparent 70%)` }}
        />
        <span className="msml-label relative z-10 text-[10px]">Overall HQ Improvement</span>
        <div className="flex items-center justify-center gap-3 mt-1.5 relative z-10">
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: beforeGrade.color }}>{Math.round(beforeHQ)}</p>
            <p className="text-[9px] text-[var(--msml-text-muted)]">Before</p>
          </div>
          <span className="text-[var(--msml-text-muted)] text-sm">&rarr;</span>
          <div className="text-center">
            <p className="text-lg font-bold" style={{ color: afterGrade.color }}>{Math.round(afterHQ)}</p>
            <p className="text-[9px] text-[var(--msml-text-muted)]">After</p>
          </div>
          <DeltaBadge value={deltaOverall} large />
        </div>
      </div>

      {/* Dimension score grid */}
      <div className="msml-card !p-3 !gap-2" data-testid="dimension-comparison-card">
        <span className="msml-label text-[10px]">Dimension Scores</span>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {dimKeys.map(key => {
            const cfg = DIMENSION_CONFIG[key] || { label: key, color: '#71717A' };
            const dimDelta = deltaDims[key];
            const bScore = dimDelta?.before ?? before?.dimension_scores?.[key] ?? 0;
            const aScore = dimDelta?.after ?? after?.dimension_scores?.[key] ?? bScore;
            const delta = dimDelta?.delta ?? (aScore - bScore);
            return (
              <div key={key} className="flex items-center justify-between gap-1 py-0.5" data-testid={`dim-compare-${key}`}>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
                  <span className="text-[10px] text-[var(--msml-text-secondary)] truncate">{cfg.label}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-[10px] text-[var(--msml-text-muted)]">{Math.round(bScore)}</span>
                  <span className="text-[8px] text-[var(--msml-text-muted)]">&rarr;</span>
                  <span className="text-[10px] font-semibold" style={{ color: getDimensionScoreColor(aScore) }}>{Math.round(aScore)}</span>
                  <DeltaBadge value={delta} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Optimized text (expanded by default, collapsible) */}
      <div className="msml-card !p-3 !gap-2" data-testid="optimized-card">
        <div className="flex items-center justify-between">
          <span className="msml-label text-[10px]">Optimized Rewrite</span>
          <div className="flex items-center gap-2">
            <button onClick={handleCopy} className="flex items-center gap-1 text-[10px] text-[var(--msml-text-muted)] hover:text-[var(--msml-text-primary)] transition-colors" data-testid="copy-optimized-btn">
              <Copy className="w-3 h-3" /> Copy
            </button>
            <button onClick={() => setTextExpanded(!textExpanded)} className="text-[var(--msml-text-muted)] hover:text-[var(--msml-text-primary)] transition-colors" data-testid="toggle-text-btn">
              {textExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>
        <p className={`text-[11px] text-[var(--msml-text-secondary)] leading-relaxed whitespace-pre-wrap ${textExpanded ? '' : 'line-clamp-[10]'}`} data-testid="optimized-text">
          {opt.text}
        </p>
      </div>
    </div>
  );
}

function DeltaBadge({ value, large }) {
  if (value == null || Math.abs(value) < 0.5) return null;
  const rounded = Math.round(value);
  const isUp = rounded > 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full font-semibold flex-shrink-0 ${
        isUp ? 'bg-[var(--msml-success)]/15 text-[var(--msml-success)]' : 'bg-[var(--msml-danger)]/15 text-[var(--msml-danger)]'
      } ${large ? 'px-2 py-0.5 text-xs' : 'px-1 py-px text-[9px]'}`}
      data-testid="delta-badge"
    >
      <Icon className={large ? 'w-3 h-3' : 'w-2.5 h-2.5'} />
      {isUp ? '+' : ''}{rounded}
    </span>
  );
}
